import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, lineHeights, fontWeights, shadows, mediaQueries
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import helperUtils from 'utils/Helpers';
import { getImageAltText } from 'utils/Accessibility';
import StringUtils from 'utils/String';
import {
    Grid, Box, Text, Button, Link, Icon, Divider
} from 'components/ui';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import AcceleratedPromoInfo from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/components/AcceleratedPromoInfo/AcceleratedPromoInfo';
import { useSubscriptionItem } from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/hooks/useSubscriptionItem';

const { capitalizeFirstLetter } = helperUtils;
const SM_IMG_SIZE = 64;
const SM_IMG_GAP = space[1];

/**
 * SubscriptionItem displays a single auto-replenishment subscription with product details,
 * pricing, delivery schedule, and subscription management actions.
 *
 * @param {Object} props - Component props
 * @param {Function} [props.onOrderNowClick] - Callback fired when "Get It Now" button is clicked, receives object with subscription data
 * @param {Function} [props.toggleManageSubscriptionModal] - Opens manage subscription modal
 * @param {Function} [props.toggleModifySubscriptionErrorModal] - Shows error modal for non-modifiable subscriptions
 * @param {Function} [props.toggleUnsubscribeModal] - Opens unsubscribe confirmation modal
 * @param {Function} [props.toggleResumeSubscriptionModal] - Opens resume subscription modal
 * @param {Function} [props.setCurrentSubscription] - Sets the currently selected subscription for modal actions
 * @param {Object} props.subscription - Subscription data object
 * @param {string} props.firstName - User's first name for personalization
 */
function SubscriptionItem({
    onOrderNowClick,
    firstName,
    setCurrentSubscription,
    subscription,
    toggleManageSubscriptionModal,
    toggleModifySubscriptionErrorModal,
    toggleResumeSubscriptionModal,
    toggleUnsubscribeModal
}) {
    const {
        featureFlags, hotDealData, localization, product, shouldShowItemVariation, subscriptionData
    } = useSubscriptionItem({
        subscription
    });

    const handleClickUnsubscribe = useCallback(() => {
        if (!subscription.isModifiable) {
            toggleModifySubscriptionErrorModal?.();
        } else {
            setCurrentSubscription?.(subscription);
            toggleUnsubscribeModal?.();
        }
    }, [subscription, setCurrentSubscription, toggleModifySubscriptionErrorModal, toggleUnsubscribeModal]);

    const handleManageSubscription = useCallback(() => {
        if (!subscription.isModifiable) {
            toggleModifySubscriptionErrorModal?.();
        } else {
            setCurrentSubscription?.(subscription);
            toggleManageSubscriptionModal?.();
        }
    }, [subscription, setCurrentSubscription, toggleModifySubscriptionErrorModal, toggleManageSubscriptionModal]);

    const handleResumeSubscription = useCallback(() => {
        setCurrentSubscription?.(subscription);
        toggleResumeSubscriptionModal?.();
    }, [subscription, setCurrentSubscription, toggleResumeSubscriptionModal]);

    const handleOrderNowClick = useCallback(() => {
        onOrderNowClick?.({ subscription });
    }, [onOrderNowClick, subscription]);

    return (
        <Box
            css={styles.itemContainer}
            key={product.skuId}
        >
            <Icon
                css={styles.icon}
                name='autoReplenish'
            />
            <Text
                css={styles.heading}
                is='h2'
            >
                {subscriptionData.isPaused && (
                    <>
                        {localization.paused}
                        <Text
                            is='span'
                            css={styles.subheading}
                        >
                            {`${capitalizeFirstLetter(firstName)}, ${product.acceleratedPromotion ? localization.stillSave : localization.save} `}
                            <Text
                                is='span'
                                css={styles.highlightRed}
                            >
                                {`${product.acceleratedPromotion ? product.acceleratedPromotion.remainingAcceleratedDiscount : product.yearlySavings} `}
                            </Text>
                            {product.acceleratedPromotion
                                ? `${localization.before} ${hotDealData.transformedPromoExpiryDate ?? ''}.`
                                : localization.annuallyWithSubscription}
                        </Text>
                    </>
                )}
                {subscriptionData.isActive && `${localization.deliverEvery} ${subscription.frequency} ${subscriptionData.frequencyDelivery}`}
            </Text>
            <Divider />
            <Grid
                gap={[`${SM_IMG_GAP}px`, 4]}
                css={styles.gridContainer}
            >
                <Box>
                    <ProductImage
                        id={product.skuId}
                        size={[SM_IMG_SIZE, 97]}
                        skuImages={product.skuImages}
                        disableLazyLoad={true}
                        altText={getImageAltText(product)}
                    />
                </Box>
                <Box>
                    <Grid
                        css={styles.content}
                        minHeight={[SM_IMG_SIZE, 0]}
                    >
                        <Box css={styles.contentLeft}>
                            <Text
                                is='p'
                                css={styles.title}
                                children={product.brandName}
                                data-at={Sephora.debug.dataAt('sku_brand')}
                            />
                            <Text
                                is='p'
                                css={styles.subtitle}
                                children={product.productName}
                                data-at={Sephora.debug.dataAt('sku_name')}
                            />
                            {shouldShowItemVariation && (
                                <Box css={styles.detailsContainer}>
                                    <Text
                                        is='p'
                                        css={styles.detailsText}
                                        data-at={Sephora.debug.dataAt('sku_size')}
                                    >
                                        {`${product.variationType}: ${product.variationValue}`}
                                        <span css={styles.detailsSeparator}>•</span>
                                        {StringUtils.format(localization.itemText, product.skuId)}
                                    </Text>
                                </Box>
                            )}
                            <Text
                                is='p'
                                data-at={Sephora.debug.dataAt('sku_qty')}
                            >
                                Qty: {product.qty}
                            </Text>
                            {subscriptionData.isActive && (
                                <Text
                                    css={styles.date}
                                    is='p'
                                    data-at={Sephora.debug.dataAt('sku_next_shipment')}
                                >
                                    {`${localization.nextShipment} ${subscriptionData.promiseDate}`}
                                </Text>
                            )}
                            {subscriptionData.isPaused && (
                                <Text
                                    css={styles.date}
                                    is='p'
                                >
                                    {`${localization.pausedDelivery} ${subscription.frequency} ${subscriptionData.frequencyDelivery}`}
                                </Text>
                            )}
                            {product.acceleratedPromotion && (
                                <AcceleratedPromoInfo
                                    localization={localization}
                                    hotDealData={hotDealData}
                                />
                            )}
                        </Box>
                        <Box css={styles.contentRight}>
                            <Box>
                                <Text
                                    is='p'
                                    css={styles.salePrice}
                                    data-at={Sephora.debug.dataAt('sku_discounted_price')}
                                >
                                    {product.savingAmount}
                                    {product.acceleratedPromotion &&
                                        !hotDealData.acceleratedDiscountReclaimPossible &&
                                        !hotDealData.isPromoExpirationSoon &&
                                        '*'}
                                </Text>
                                <Text
                                    is='del'
                                    css={styles.listPrice}
                                    data-at={Sephora.debug.dataAt('sku_price')}
                                >
                                    {product.productPrice}
                                </Text>
                            </Box>
                        </Box>
                    </Grid>
                    <Box css={[styles.ctaContainer, featureFlags.isOrderNowEnabled && styles.ctaContainerOrderNow]}>
                        <Button
                            size='sm'
                            variant='secondary'
                            css={styles.secondaryCta}
                            onClick={subscriptionData.isPaused ? handleResumeSubscription : handleManageSubscription}
                        >
                            {subscriptionData.isPaused ? localization.resumeSubscription : localization.manageSubscription}
                        </Button>

                        {featureFlags.isOrderNowEnabled && onOrderNowClick && (
                            <Button
                                size='sm'
                                variant='secondary'
                                css={styles.secondaryCta}
                                onClick={handleOrderNowClick}
                            >
                                {localization.getItNow}
                            </Button>
                        )}
                        {!featureFlags.isOrderNowEnabled && (
                            <Link
                                css={styles.blueLink}
                                onClick={handleClickUnsubscribe}
                            >
                                {localization.unsubscribe}
                            </Link>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Box>
    );
}

const styles = {
    itemContainer: {
        boxShadow: shadows.light,
        padding: `${space[1]}px ${space[4]}px`,
        marginTop: space[4],
        borderRadius: space[2]
    },
    icon: {
        verticalAlign: 'middle',
        marginRight: space[3]
    },
    heading: {
        display: 'inline-block',
        verticalAlign: 'middle',
        paddingTop: space[2],
        paddingBottom: space[3],
        fontSize: fontSizes.md,
        fontWeight: fontWeights.bold,
        maxWidth: '85%',
        lineHeight: lineHeights.tight
    },
    subheading: {
        fontWeight: 'normal',
        display: 'block',
        whiteSpace: 'nowrap'
    },
    highlightRed: {
        color: colors.red,
        fontWeight: fontWeights.bold
    },
    gridContainer: {
        gridTemplateColumns: 'auto 1fr',
        padding: `${space[3]}px 0`,
        lineHeight: lineHeights.tight,
        alignItems: 'start'
    },
    content: {
        gap: space[2],
        gridTemplateColumns: '1fr auto'
    },
    contentLeft: {
        padding: space[2]
    },
    title: {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold
    },
    subtitle: {
        fontSize: fontSizes.base,
        marginBottom: space[1],
        lineHeight: lineHeights.tight
    },
    detailsContainer: {
        color: colors.gray
    },
    detailsText: {
        display: 'inline-block',
        lineHeight: lineHeights.tight
    },
    detailsSeparator: {
        marginLeft: space[2],
        marginRight: space[2]
    },
    date: {
        marginTop: space[2],
        whiteSpace: 'nowrap'
    },
    contentRight: {
        textAlign: 'right'
    },
    salePrice: {
        fontWeight: fontWeights.bold,
        marginTop: space[2],
        color: colors.red
    },
    listPrice: {
        color: colors.black
    },
    ctaContainer: {
        marginTop: space[4],
        display: 'flex',
        flexWrap: 'wrap',
        rowGap: space[2],
        columnGap: space[4],
        alignItems: 'center'
    },
    ctaContainerOrderNow: {
        columnGap: space[3],
        [mediaQueries.sm]: {
            columnGap: space[2]
        }
    },
    secondaryCta: {
        borderWidth: '1px',
        fontWeight: 'normal'
    },
    blueLink: {
        color: colors.blue,
        fontSize: fontSizes.base
    }
};

SubscriptionItem.propTypes = {
    onOrderNowClick: PropTypes.func,
    toggleManageSubscriptionModal: PropTypes.func,
    toggleModifySubscriptionErrorModal: PropTypes.func,
    toggleUnsubscribeModal: PropTypes.func,
    toggleResumeSubscriptionModal: PropTypes.func,
    setCurrentSubscription: PropTypes.func,
    subscription: PropTypes.shape({
        isModifiable: PropTypes.bool,
        frequency: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        frequencyType: PropTypes.string,
        nextScheduleRunDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        items: PropTypes.arrayOf(
            PropTypes.shape({
                skuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                brandName: PropTypes.string,
                productName: PropTypes.string,
                skuImages: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
                price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                discountAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                discountedPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                discountType: PropTypes.string,
                yearlySavings: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                acceleratedPromotion: PropTypes.shape({
                    remainingOrderCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    remainingAcceleratedDiscount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    promoExpirationDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
                    discountAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    acceleratedDiscountReclaimPossible: PropTypes.bool
                })
            })
        )
    }).isRequired,
    firstName: PropTypes.string.isRequired
};

export default wrapFunctionalComponent(SubscriptionItem, 'SubscriptionItem');
